/**
 * Notification TanStack Query Hooks
 *
 * Provides useInfiniteQuery for paginated list, useQuery for unread count,
 * and useMutation hooks for mark read, mark all read, and dismiss.
 * All mutations use optimistic updates with rollback on error.
 *
 * @module entities/notification/hooks/useNotificationQueries
 */

import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  notificationQueryKeys,
  queryOptions as qo,
} from '@lib/query/client';
import * as notificationApi from '../services/notificationApi';
import type {
  Notification,
  NotificationListFilters,
  NotificationListResponse,
  UnreadCountResponse,
} from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Query Hooks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch paginated notifications using cursor-based infinite query
 *
 * @param filters - category and status filters
 * @param enabled - whether to run the query (default: true)
 */
export function useNotifications(
  filters: Omit<NotificationListFilters, 'cursor'> = {},
  enabled: boolean = true
) {
  return useInfiniteQuery<NotificationListResponse, Error>({
    queryKey: notificationQueryKeys.notifications.list({ ...filters }),
    queryFn: ({ pageParam }) =>
      notificationApi.getNotifications({
        ...filters,
        cursor: pageParam as string | undefined,
        limit: filters.limit || 15,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    ...qo.notifications,
  });
}

/**
 * Fetch unread notification count for badge display
 *
 * @param enabled - whether to run the query (default: true)
 */
export function useUnreadCount(enabled: boolean = true) {
  return useQuery<UnreadCountResponse, Error>({
    queryKey: notificationQueryKeys.notifications.unreadCount(),
    queryFn: notificationApi.getUnreadCount,
    enabled,
    ...qo.notificationUnreadCount,
    // Poll as SSE fallback -- if SSE drops, badge still updates
    refetchInterval: 60 * 1000,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Cache Helpers (shared between mutations and SSE hook)
// ─────────────────────────────────────────────────────────────────────────────

type NotificationPages = InfiniteData<NotificationListResponse>;

/**
 * Mark a single notification as read in all cached pages
 */
export function markNotificationReadInCache(
  pages: NotificationPages,
  notificationId: string
): NotificationPages {
  return {
    ...pages,
    pages: pages.pages.map((page) => ({
      ...page,
      notifications: page.notifications.map((n) =>
        n.id === notificationId
          ? { ...n, read: true, readAt: new Date().toISOString() }
          : n
      ),
    })),
  };
}

/**
 * Mark all notifications as read in all cached pages
 */
export function markAllReadInCache(
  pages: NotificationPages
): NotificationPages {
  return {
    ...pages,
    pages: pages.pages.map((page) => ({
      ...page,
      notifications: page.notifications.map((n) => ({
        ...n,
        read: true,
        readAt: n.readAt || new Date().toISOString(),
      })),
      totalUnread: 0,
    })),
  };
}

/**
 * Remove a notification from all cached pages
 */
export function removeNotificationFromCache(
  pages: NotificationPages,
  notificationId: string
): NotificationPages {
  return {
    ...pages,
    pages: pages.pages.map((page) => ({
      ...page,
      notifications: page.notifications.filter((n) => n.id !== notificationId),
    })),
  };
}

/**
 * Prepend a new notification to the first page in cache
 */
export function prependNotificationToCache(
  pages: NotificationPages,
  notification: Notification
): NotificationPages {
  if (!pages.pages.length) return pages;

  return {
    ...pages,
    pages: [
      {
        ...pages.pages[0],
        notifications: [notification, ...pages.pages[0].notifications],
        totalUnread: pages.pages[0].totalUnread + 1,
      },
      ...pages.pages.slice(1),
    ],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutation Hooks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Mark a single notification as read with optimistic update
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationApi.markAsRead(id),

    onMutate: async (id: string) => {
      // Cancel outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.notifications.all,
      });

      // Snapshot current state for rollback
      const previousLists = queryClient.getQueriesData<NotificationPages>({
        queryKey: notificationQueryKeys.notifications.lists(),
      });
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(
        notificationQueryKeys.notifications.unreadCount()
      );

      // Find the notification to check if it was already read
      let wasUnread = false;
      for (const [, data] of previousLists) {
        if (!data) continue;
        for (const page of data.pages) {
          const found = page.notifications.find((n) => n.id === id);
          if (found && !found.read) {
            wasUnread = true;
            break;
          }
        }
        if (wasUnread) break;
      }

      // Optimistic update: mark as read in all list caches
      queryClient.setQueriesData<NotificationPages>(
        { queryKey: notificationQueryKeys.notifications.lists() },
        (old) => (old ? markNotificationReadInCache(old, id) : old)
      );

      // Decrement unread count only if it was actually unread
      if (wasUnread) {
        queryClient.setQueryData<UnreadCountResponse>(
          notificationQueryKeys.notifications.unreadCount(),
          (old) => ({ count: Math.max((old?.count ?? 1) - 1, 0) })
        );
      }

      return { previousLists, previousCount };
    },

    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          notificationQueryKeys.notifications.unreadCount(),
          context.previousCount
        );
      }
    },
  });
}

/**
 * Mark all notifications as read with optimistic update
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationApi.markAllAsRead(),

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.notifications.all,
      });

      const previousLists = queryClient.getQueriesData<NotificationPages>({
        queryKey: notificationQueryKeys.notifications.lists(),
      });
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(
        notificationQueryKeys.notifications.unreadCount()
      );

      // Optimistic: mark all as read
      queryClient.setQueriesData<NotificationPages>(
        { queryKey: notificationQueryKeys.notifications.lists() },
        (old) => (old ? markAllReadInCache(old) : old)
      );

      // Set count to zero
      queryClient.setQueryData<UnreadCountResponse>(
        notificationQueryKeys.notifications.unreadCount(),
        { count: 0 }
      );

      return { previousLists, previousCount };
    },

    onError: (_err, _vars, context) => {
      // On error, invalidate everything to get fresh data
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          notificationQueryKeys.notifications.unreadCount(),
          context.previousCount
        );
      }
    },
  });
}

/**
 * Dismiss a notification with optimistic removal
 */
export function useDismissNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationApi.dismissNotification(id),

    onMutate: async (id: string) => {
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.notifications.all,
      });

      const previousLists = queryClient.getQueriesData<NotificationPages>({
        queryKey: notificationQueryKeys.notifications.lists(),
      });
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(
        notificationQueryKeys.notifications.unreadCount()
      );

      // Check if the dismissed notification was unread
      let wasUnread = false;
      for (const [, data] of previousLists) {
        if (!data) continue;
        for (const page of data.pages) {
          const found = page.notifications.find((n) => n.id === id);
          if (found && !found.read) {
            wasUnread = true;
            break;
          }
        }
        if (wasUnread) break;
      }

      // Optimistic: remove from cache
      queryClient.setQueriesData<NotificationPages>(
        { queryKey: notificationQueryKeys.notifications.lists() },
        (old) => (old ? removeNotificationFromCache(old, id) : old)
      );

      // Decrement count if was unread
      if (wasUnread) {
        queryClient.setQueryData<UnreadCountResponse>(
          notificationQueryKeys.notifications.unreadCount(),
          (old) => ({ count: Math.max((old?.count ?? 1) - 1, 0) })
        );
      }

      return { previousLists, previousCount };
    },

    onError: (_err, _id, context) => {
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          notificationQueryKeys.notifications.unreadCount(),
          context.previousCount
        );
      }
    },
  });
}
